import React, { useState } from "react";
import LogoFindYourFare from "../../../assets/images/findyoufare_logo.png";
import LogoAirConnectTravel from "../../../assets/images/airconnecttravel_logo.png";
import PrimaryButton from "../../../components/common/buttons/PrimaryButton";
import { Mail, MessageCircle } from "lucide-react"; // ✅ Imported MessageCircle for UI
import { useLocation } from "react-router-dom";
import { Booking } from "../../../types/types";
import { getAirPortName } from "../../../utils/airportNameByCode";
import { formatToISODate } from "../../../utils/formatDate";
import axios from "axios";
import { generateTicketHtml } from "../emailTicketTempalate/ticketTempalate";
import PopUp from "../../../components/common/popup/PopUp";
import LoadingIndicator from "../../../components/common/loadingIndicator/LoadingIndicator";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const getTravelClassName = (travelClass: string): string => {
  switch (travelClass) {
    case "Y":
    case "M":
      return "Economy";
    case "W":
      return "Premium Economy";
    case "C":
      return "Business Class";
    case "F":
      return "First Class";
    default:
      return "Unknown Class";
  }
};

const Ticket: React.FC = () => {
  const location = useLocation();
  const booking = location.state?.booking as Booking;
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const agencyInfo = {
    name: "Air Connect Travel",
    address: "Via Salvator Rosa 340, IT-80135 Napoli",
    tel: "3478380862",
    emails: ["info@airconnecttravel.com"],
  };

  const calculateDuration = (
    duration: string | number | undefined,
    departure: string,
    arrival: string,
  ): string => {
    if (duration) {
      const durStr = String(duration).trim();

      if (
        durStr.toLowerCase().includes("h") ||
        durStr.toLowerCase().includes("m")
      ) {
        return durStr.toUpperCase();
      }

      if (durStr.length === 4 && !isNaN(Number(durStr))) {
        const hours = parseInt(durStr.slice(0, 2), 10);
        const minutes = parseInt(durStr.slice(2, 4), 10);
        return `${hours}H ${minutes}M`;
      }

      if (!isNaN(Number(durStr))) {
        const totalMinutes = parseInt(durStr, 10);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}H ${minutes}M`;
      }
    }

    if (!departure || !arrival) {
      return "N/A";
    }

    const depTime = new Date(departure);
    const arrTime = new Date(arrival);
    let diffMs = arrTime.getTime() - depTime.getTime();

    if (diffMs < 0) {
      diffMs += 24 * 60 * 60 * 1000;
    }

    const diffMins = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;

    return `${hours}H ${minutes}M`;
  };

  const formatTimeAMPM = (dateString: string) => {
    if (!dateString) return "";
    return parseExactLocalTime(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatSafeDate = (dateString: string) => {
    if (!dateString) return "";
    return parseExactLocalTime(dateString)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .toUpperCase();
  };

  const parseExactLocalTime = (dateString: string) => {
    if (!dateString) return new Date();
    const cleanStr = dateString.replace(/(Z|[+-]\d{2}:?\d{2})$/, "");
    return new Date(cleanStr);
  };

  const downloadPdf = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;

    for (
      let index = 0;
      index < booking.bookingDetails.passengers.length;
      index++
    ) {
      const passengerElement = document.getElementById(`ticket-${index}`);

      if (!passengerElement) {
        alert(`Ticket content not available for passenger ${index + 1}.`);
        return;
      }

      window.scrollTo(0, 0);

      const canvas = await html2canvas(passengerElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        height: passengerElement.scrollHeight,
        windowHeight: passengerElement.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const imgProps = pdf.getImageProperties(imgData);

      const availableWidth = pdfWidth - margin * 2;
      const availableHeight = pdfHeight - margin * 2;

      const widthRatio = availableWidth / imgProps.width;
      const heightRatio = availableHeight / imgProps.height;
      const ratio = Math.min(widthRatio, heightRatio, 1);

      const finalWidth = imgProps.width * ratio;
      const finalHeight = imgProps.height * ratio;

      const x = (pdfWidth - finalWidth) / 2;
      const y = margin;

      pdf.addImage(imgData, "PNG", x, y, finalWidth, finalHeight);

      if (index < booking.bookingDetails.passengers.length - 1) {
        pdf.addPage();
      }
    }

    pdf.save("Ticket.pdf");
  };

  const sendEmailWithPdf = async () => {
    setLoading(true);

    const ticketHtml = generateTicketHtml(booking, agencyInfo);
    const API_URL = process.env.REACT_APP_API_BASE_URL;

    if (ticketHtml) {
      try {
        // ✅ FIX: Extract Email and Contact safely
        const userEmail =
          booking.user?.email || booking.bookingDetails?.guestEmail;
        const userContact =
          booking.user?.contact || booking.bookingDetails?.guestContact;
        const paxName =
          booking.user?.firstName ||
          booking.bookingDetails.passengers[0]?.firstName ||
          "Traveler";

        await axios.post(`${API_URL}/notification/send-ticket-email`, {
          email: userEmail,
          contact: userContact, // ✅ Sent to backend
          passengerName: paxName, // ✅ Sent to backend
          controlNumber: booking.controlNumber, // ✅ Sent to backend
          subject: "Your Flight Ticket",
          htmlTemplate: ticketHtml,
        });

        // ✅ Dynamic Success Message
        if (userEmail && userContact) {
          setPopupMessage("Ticket sent successfully via Email & WhatsApp!");
        } else if (userContact) {
          setPopupMessage("Ticket sent successfully via WhatsApp!");
        } else {
          setPopupMessage("Ticket sent successfully via Email!");
        }
      } catch (error) {
        console.error("Error sending email/whatsapp:", error);
        setPopupMessage("Failed to send ticket. Please try again.");
      } finally {
        setLoading(false);
        setShowPopup(true);
      }
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  return (
    <div>
      {/* Ticket Content */}
      <div id="ticket">
        {booking.bookingDetails.passengers.map((passenger, index) => (
          <div
            id={`ticket-${index}`}
            className="cw-full max-w-4xl mx-auto bg-white p-8 pb-24 shadow-lg"
            key={index}
          >
            <div className="grid grid-cols-3 gap-4 mb-10">
              <div className="flex justify-between items-center mb-6">
                <img
                  src={LogoFindYourFare}
                  alt="Find Your Fares Logo"
                  className="h-14"
                />
              </div>
              <h4 className="text-2xl text-center font-bold mb-9 mt-3 tracking-wide">
                e-Ticket and Itinerary
              </h4>
              <div className="text-left mb-4">
                <h2 className="text-sm text-right font-bold mt-3">
                  <span className="text-sm font-light">Booking Reference</span>{" "}
                  {booking.controlNumber}
                </h2>
                <h2 className="text-sm text-right font-bold mt-3">
                  <span className="text-sm font-light">Ticket Number</span>{" "}
                  {passenger.ticketNumber || booking.ticketNumber || "Pending"}
                </h2>
              </div>
            </div>
            <p>
              This e-Ticket receipt/itinerary is for your record and forms part
              of your contract of carriage.
            </p>
            <br />
            <p>
              Please ensure you complete check-in at least 90 minutes before
              departure. Boarding begins 45 minutes before your flight, and
              gates close 20 minutes prior. Late arrivals may not be
              accommodated.
            </p>
            <br />
            <h3 className="bg-[#cccff2] font-semibold px-4 py-2 uppercase text-sm">
              passenger and ticket information
            </h3>
            <div className="grid grid-cols-4 gap-1">
              <div className="p-4 flex flex-col gap-1 w-min text-nowrap text-sm uppercase">
                <p>Passenger name</p>
                <p>Passenger Type</p>
                <p>Passport Expiry Date</p>
              </div>
              <div className="p-4 flex flex-col gap-1 w-min text-nowrap text-sm uppercase">
                <p>
                  <strong>
                    {passenger.lastName}
                    {"/"}
                    {passenger.firstName}
                  </strong>
                </p>
                <p>
                  <strong>{passenger.type}</strong>
                </p>
                <p>
                  <strong>{passenger.passportExpiry}</strong>
                </p>
              </div>
              <div className="p-4 flex flex-col gap-1 w-min text-nowrap text-sm uppercase">
                <p>
                  <br />
                </p>
                <p className="text-left">Booking reference</p>
                <p>Issued Date</p>
              </div>
              <div className="p-4 flex flex-col gap-1 w-min text-nowrap text-sm uppercase">
                <p>
                  <br />
                </p>
                <p>
                  {" "}
                  <strong>{booking.controlNumber}</strong>
                </p>
                <p>
                  <strong>
                    {formatSafeDate(
                      booking.bookingDetails.createdAt.toString(),
                    )}
                  </strong>
                </p>
              </div>
            </div>
            <h3 className="bg-[#cccff2] font-semibold px-4 py-2 uppercase text-sm">
              Travel information
            </h3>
            <div className="overflow-x-auto">
              <table className="table-auto w-full text-sm text-left">
                <thead className="uppercase">
                  <tr>
                    <th className="px-4 py-2 align-middle">Flight</th>
                    <th className="px-4 py-2 align-middle">Depart/Arrive</th>
                    <th className="px-4 py-2 align-middle">Duration</th>
                    <th className="px-4 py-2 align-middle">Airport</th>
                    <th className="px-4 py-2 align-middle">Class</th>
                  </tr>
                </thead>
                <tbody>
                  {booking.bookingDetails.flights.map((flight, index) => (
                    <React.Fragment key={index}>
                      {/* First Row: Departure */}
                      <tr className="bg-[#f3f4ff]">
                        <td className="px-4 py-2 font-bold uppercase">
                          {flight.airline} {flight.flightNumber}
                          <br />
                          <span className="font-normal">Confirmed</span>
                        </td>
                        <td className="px-4 py-2">
                          <strong>
                            {formatSafeDate(flight.departureDateTime || "")}
                          </strong>
                          <br />
                          <span>
                            <strong>
                              {formatTimeAMPM(flight.departureDateTime || "")}
                            </strong>
                          </span>
                        </td>
                        <td className="px-4 py-2 uppercase">
                          {calculateDuration(
                            flight.duration,
                            flight.departureDateTime || "",
                            flight.arrivalDateTime || "",
                          )}{" "}
                        </td>
                        <td className="px-4 py-2 uppercase">
                          {getAirPortName(flight.origin)} ({flight.origin})
                        </td>
                        <td className="px-4 py-2 uppercase">
                          {getTravelClassName(
                            booking.bookingDetails.travelClass,
                          )}{" "}
                          - {flight.rbd}
                        </td>
                      </tr>

                      {/* Second Row: Arrival */}
                      <tr>
                        <td className="px-4 py-2"></td>
                        <td className="px-4 py-2 uppercase">
                          <strong>
                            {formatSafeDate(flight.arrivalDateTime || "")}
                          </strong>
                          <br />
                          <span>
                            <strong>
                              {formatTimeAMPM(flight.arrivalDateTime || "")}
                            </strong>
                          </span>
                        </td>
                        <td className="px-4 py-2"></td>
                        <td className="px-4 py-2 uppercase">
                          {getAirPortName(flight.destination)} (
                          {flight.destination})
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <h3 className="bg-[#cccff2] font-semibold px-4 py-2 uppercase text-sm mt-4">
              Baggage Allowance Information
            </h3>
            <div className="p-3 text-sm">
              {booking.baggageDetails ? (
                <div className="space-y-1">
                  {booking.baggageDetails.outbound && (
                    <p>
                      <strong>Outbound Flights:</strong>
                      {booking.baggageDetails.outbound.adult && (
                        <span>
                          {" "}
                          <strong>Adult:</strong>{" "}
                          {booking.baggageDetails.outbound.adult.allowance}{" "}
                          {booking.baggageDetails.outbound.adult.unit}
                        </span>
                      )}
                      {booking.baggageDetails.outbound.child && (
                        <span>
                          {" "}
                          , <strong>Child:</strong>{" "}
                          {booking.baggageDetails.outbound.child.allowance}{" "}
                          {booking.baggageDetails.outbound.child.unit}
                        </span>
                      )}
                      {booking.baggageDetails.outbound.infant && (
                        <span>
                          {" "}
                          , <strong>Infant:</strong>{" "}
                          {booking.baggageDetails.outbound.infant.allowance}{" "}
                          {booking.baggageDetails.outbound.infant.unit}
                        </span>
                      )}
                    </p>
                  )}
                  {booking.baggageDetails.return && (
                    <p>
                      <strong>Return Flights:</strong>
                      {booking.baggageDetails.return.adult && (
                        <span>
                          {" "}
                          <strong>Adult:</strong>{" "}
                          {booking.baggageDetails.return.adult.allowance}{" "}
                          {booking.baggageDetails.return.adult.unit}
                        </span>
                      )}
                      {booking.baggageDetails.return.child && (
                        <span>
                          {" "}
                          , <strong>Child:</strong>{" "}
                          {booking.baggageDetails.return.child.allowance}{" "}
                          {booking.baggageDetails.return.child.unit}
                        </span>
                      )}
                      {booking.baggageDetails.return.infant && (
                        <span>
                          {" "}
                          , <strong>Infant:</strong>{" "}
                          {booking.baggageDetails.return.infant.allowance}{" "}
                          {booking.baggageDetails.return.infant.unit}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              ) : (
                <p>
                  General Allowance: {booking.bookingDetails.baggaeAllownce}
                </p>
              )}
            </div>

            <div className="border- border-gray-200"></div>
            <div
              className="grid grid-cols-2 gap-4 mt-8 break-inside-avoid"
              style={{ pageBreakInside: "avoid" }}
            >
              <div className="p-4 flex flex-col gap-1 w-min text-nowrap text-sm uppercase">
                <h3 className="text-sm font-bold mb-1">Agency Details :</h3>
                <p className="text-xs">{agencyInfo.name}</p>
                <p className="text-xs">{agencyInfo.address}</p>
                <p className="text-xs">Tel: {agencyInfo.tel}</p>
                {agencyInfo.emails.map((email, idx) => (
                  <p className="text-xs" key={idx}>
                    {email}
                  </p>
                ))}
              </div>
              <div className="p-4 flex justify-end items-center">
                <img
                  src={LogoAirConnectTravel}
                  alt="Air Connect Travel Logo"
                  className="h-40"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {loading && <LoadingIndicator />}

      <div className="cw-full max-w-4xl mx-auto ml-auto text-nowrap p-2 bg-white my-2 flex gap-4 w-full">
        <div className="w-1/2">
          <PrimaryButton onClick={downloadPdf}>
            <div className="flex justify-center gap-4">
              <Mail /> Download PDF
            </div>
          </PrimaryButton>
        </div>
        <div className="w-1/2">
          <PrimaryButton onClick={sendEmailWithPdf}>
            <div className="flex justify-center gap-4">
              <Mail /> <MessageCircle /> Send Ticket
            </div>
          </PrimaryButton>
        </div>
      </div>
      {showPopup && (
        <PopUp
          message={popupMessage}
          onClose={closePopup}
          onConfirm={closePopup}
        />
      )}
    </div>
  );
};

export default Ticket;
